import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cx from 'classnames';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {selectEditingMobile} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {DrawerBottomSheet} from '~/CreateFabricInvitation/components/DrawerBottomSheet/DrawerBottomSheet';
import {Button} from '~library/atoms/Button';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {Typography} from '~library/atoms/Typography';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';

import './EditorContainer.sass';

const EditorTab = ({title, isActive, onClick, index}) => (
  <Button
    data-qa-id={`design-editor-tab-${index}`}
    variant="unstyled-button"
    className={cx('mobile-editor__tab', {
      'mobile-editor__tab--active': isActive,
    })}
    onClick={onClick}
  >
    <Typography
      variant="label2"
      className={cx('mobile-editor__tab__label', {
        'mobile-editor__tab__label--active': isActive,
      })}
      color={isActive ? 'charcoal' : 'pebble'}
    >
      {title || `Tab ${index + 1}`}
    </Typography>
  </Button>
);

export const EditorContainer = ({children, flipEnvelope, scrolls}) => {
  const editingMobile = useSelector(selectEditingMobile);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const mobileWrapperRef = useRef(null);
  const navigate = useNavigator({isMobile: true, disableAutoOpenEditor: true});
  const {inIsolatedEditMode} = useContext(FabricContext);

  const handleClose = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
    if (inIsolatedEditMode) navigate(Sections.CARD_DESIGN);
  }, [dispatch, inIsolatedEditMode, navigate]);

  const tabs = Array.isArray(children) ? children : [children];

  const handleTabChange = useCallback(
    (tab, index) => {
      setActiveTab(index);
      if (flipEnvelope) {
        flipEnvelope(!!tab.props.showEnvelopeFront);
      }
    },
    [flipEnvelope]
  );

  const renderHeader = () => (
    <div className="mobile-editor__header">
      <div className="mobile-editor__tabs">
        {tabs.map((tab, index) => (
          <EditorTab
            key={tab.props.title || index}
            title={tab.props.title}
            isActive={activeTab === index}
            onClick={() => handleTabChange(tab, index)}
            index={index}
          />
        ))}
      </div>
      <Button
        className="unstyled-button mobile-editor__close_button"
        onClick={handleClose}
        data-qa-id="design-editor-close"
        type="button"
      >
        <CloseLineIcon ratio={0.75} />
      </Button>
    </div>
  );

  useEffect(() => {
    if (mobileWrapperRef.current) mobileWrapperRef.current.scrollTop = 0;
  }, [activeTab]);

  if (!editingMobile) {
    return null;
  }

  return (
    <DrawerBottomSheet
      isOpen={editingMobile}
      onClose={handleClose}
      className="mobile-editor"
      header={renderHeader()}
      minHeightPercentage={30}
      maxHeightPercentage={70}
    >
      <div
        className={cx('mobile-editor__content', {
          'mobile-editor__content--scrolls': scrolls,
        })}
        ref={mobileWrapperRef}
      >
        {tabs[activeTab]}
      </div>
    </DrawerBottomSheet>
  );
};
